import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { useEvaluateAnalysisPortfolio } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { ContractProposalPortfolio, NsurePlanOverview } from "Infrastructure/ApiZfp/Schema";
import { EvaluatePortfolioBadRequestErrorCode } from "Infrastructure/ApiZfp/Schema/evaluatePortfolioBadRequestErrorCode";
import { PortfolioEvaluationItemPlanEvaluationType } from "Infrastructure/ApiZfp/Schema/portfolioEvaluationItemPlanEvaluationType";
import { useContext, useEffect } from "react";

type Params = {
  portfolio: ContractProposalPortfolio | null | undefined;
  oneTimeAmount: number;
  regularAmount: number;
  nsurePlan: NsurePlanOverview | undefined;
};

export const useProposalNsureEvaluation = ({ portfolio, oneTimeAmount, regularAmount, nsurePlan }: Params) => {
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { mutate, data, isPending, isError, error } = useEvaluateAnalysisPortfolio({
    // Evaluation errors (including 500) are shown inline on the item instead of the global toast.
    mutation: { onError: () => undefined },
  });

  useEffect(() => {
    if (!portfolio) return;

    mutate({
      analysisId,
      versionId,
      data: {
        externalID: nsurePlan?.nsureInvestmentPlanExternalId ?? undefined,
        investmentAmounts: [
          { payment: oneTimeAmount, type: "OneTime" },
          { payment: regularAmount, type: "Frequency" },
        ],
        investmentStrategies: [
          {
            oneTimeParticipationInCurrency: oneTimeAmount,
            periodicParticipationInCurrency: regularAmount,
            oneTimeParticipationInPercentage: portfolio.oneTimeParticipationInPercentage ?? 100,
            periodicParticipationInPercentage: portfolio.periodicParticipationInPercentage ?? 100,
            strategyInstruments: portfolio.strategyInstruments.map((si) => ({
              instrumentID: si.instrumentID,
              participationInPercentage: si.participationInPercentage,
              oneTimeParticipationInCurrency: oneTimeAmount * (si.participationInPercentage / 100),
              periodicParticipationInCurrency: regularAmount * (si.participationInPercentage / 100),
            })),
          },
        ],
      },
    });
  }, [portfolio, oneTimeAmount, regularAmount, nsurePlan, analysisId, versionId, mutate]);

  if (!portfolio) return { hasWarning: false, noQuestionnaireEvaluation: false, hasError: false, isPending: false };

  if (isError) {
    if (error?.code === EvaluatePortfolioBadRequestErrorCode.questionnaire_not_evaluated) {
      return { hasWarning: false, noQuestionnaireEvaluation: true, hasError: false, isPending: false };
    }
    return { hasWarning: false, noQuestionnaireEvaluation: false, hasError: true, isPending: false };
  }

  const items = data?.evaluationInvestmentPlan?.questionnaireEvaluationItems ?? [];
  const hasWarning = items.some((item) => item.planEvaluationType !== PortfolioEvaluationItemPlanEvaluationType.Match);

  return { hasWarning, noQuestionnaireEvaluation: false, hasError: false, isPending };
};
